import React, { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Shield, FileText, Mic, CheckCircle2, AlertCircle, ExternalLink, Copy, Check, ClipboardList, HelpCircle, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { safeCopyToClipboard } from '../../utils/clipboard';
import { toast } from 'sonner';

const STEPS = [
  { id: 'need', label: 'What You Need', icon: ClipboardList, description: 'Select your situation' },
  { id: 'details', label: 'Your Story', icon: Mic, description: 'Describe your situation' },
  { id: 'service', label: 'Your Info', icon: Shield, description: 'Your military background' },
  { id: 'results', label: 'Your Documents', icon: FileText, description: 'Review and copy' },
];

const VA_NEEDS = [
  { value: 'new_claim', label: 'File a New Disability Claim', category: 'Claims',
    icon: '1',
    desc: 'First time filing, or adding a new condition',
    guidance: 'Describe the condition, when it started, how it connects to your service, and how it affects your daily life.' },
  { value: 'increase_rating', label: 'Increase an Existing Rating', category: 'Claims',
    icon: '2',
    desc: 'Your condition has gotten worse since your last rating',
    guidance: 'What is your current rating and for what condition? What has changed or worsened? What new evidence do you have?' },
  { value: 'supplemental_claim', label: 'Appeal with New Evidence (Supplemental)', category: 'Claims',
    icon: '3',
    desc: 'You have new medical evidence to support a denied or low-rated claim',
    guidance: 'What was denied or rated too low? What new evidence do you have (new diagnosis, nexus letter, buddy statement)?' },
  { value: 'higher_level_review', label: 'Higher-Level Review (No New Evidence)', category: 'Claims',
    icon: '4',
    desc: 'You believe VA made an error — wrong diagnostic code, ignored evidence, or misapplied the law',
    guidance: 'What specific error did the VA make? Reference the diagnostic code and explain what was wrong.' },
  { value: 'medication_approval', label: 'Get a Medication Approved / Prior Auth', category: 'Medication',
    icon: 'Rx',
    desc: 'VA denied or delayed a medication you need',
    guidance: 'What medication was denied? What did you try before? Why does your provider say you need this specific one?' },
  { value: 'personal_statement', label: 'Write a Personal Statement (21-4138)', category: 'Statements',
    icon: 'PS',
    desc: 'Your own written statement describing how a condition affects your daily life',
    guidance: 'Describe frequency of symptoms, severity (1-10), what you can\'t do anymore, how it affects work and family.' },
  { value: 'buddy_statement', label: 'Help a Buddy Write a Statement (21-10210)', category: 'Statements',
    icon: 'BS',
    desc: 'A spouse, friend, or battle buddy statement supporting your claim',
    guidance: 'Who is writing this? What have they personally witnessed? How long have they known you?' },
  { value: 'records_request', label: 'Request My Service Records', category: 'Records',
    icon: 'SR',
    desc: 'Get copies of your military medical records, DD-214, or service treatment records',
    guidance: 'What records do you need and for what purpose? Where were you treated during service?' },
];

const DISCHARGE_TYPES = [
  { value: 'honorable', label: 'Honorable' },
  { value: 'general', label: 'General (Under Honorable Conditions)' },
  { value: 'other_than_honorable', label: 'Other Than Honorable (OTH)' },
  { value: 'bad_conduct', label: 'Bad Conduct' },
  { value: 'medical', label: 'Medical Retirement / MEB' },
  { value: 'still_serving', label: 'Still Serving (Active Duty / Reserve)' },
];

const BRANCHES = [
  'Army', 'Navy', 'Air Force', 'Marine Corps', 'Coast Guard', 'Space Force', 'National Guard', 'Reserve',
];

const ENCOURAGEMENT = [
  "You served your country. These benefits exist because you earned them.",
  "Many Veterans feel unsure about filing. That's normal — the system is complex, but you don't have to navigate it alone.",
  "A denied claim is not the end. Most successful claims required at least one appeal.",
  "You don't need to have a \"dramatic\" injury. Chronic conditions from service — hearing loss, back pain, sleep issues, mental health — all count.",
  "Getting your records organized is half the battle. You're already doing the right thing by being here.",
];

const VA_RESOURCES = [
  { label: 'VA.gov - File a Claim', url: 'https://www.va.gov/disability/how-to-file-claim/', desc: 'Official claim filing portal' },
  { label: 'VA.gov - Check Claim Status', url: 'https://www.va.gov/claim-or-appeal-status/', desc: 'Track your existing claim' },
  { label: 'VA.gov - Decision Reviews', url: 'https://www.va.gov/decision-reviews/', desc: 'Supplemental claims, HLR, Board appeals' },
  { label: 'eBenefits', url: 'https://www.ebenefits.va.gov/', desc: 'Benefits portal and records' },
  { label: 'VA Form Finder', url: 'https://www.va.gov/find-forms/', desc: 'Search all VA forms' },
  { label: 'Request Military Records (SF-180)', url: 'https://www.archives.gov/veterans/military-service-records', desc: 'National Personnel Records Center' },
  { label: '38 CFR Part 4 - Rating Schedule', url: 'https://www.ecfr.gov/current/title-38/chapter-I/part-4', desc: 'The criteria VA uses to assign ratings' },
  { label: 'VA Health Care Enrollment', url: 'https://www.va.gov/health-care/how-to-apply/', desc: 'Enroll in VA health benefits' },
];

const VAClaimsWizard = ({
  onInsertText,
  notes,
  setNotes,
  generateDocuments,
  isGenerating,
  result,
  setGenerateAdminLetter,
  setLetterType,
  setDocumentMode,
  setMessageScenario,
  setLetterContext,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [serviceInfo, setServiceInfo] = useState({
    branch: '', yearsOfService: '', dischargeType: '', serviceStart: '', serviceEnd: '',
  });
  const [selectedNeed, setSelectedNeed] = useState('');
  const [details, setDetails] = useState('');
  const [denialText, setDenialText] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  const encouragement = ENCOURAGEMENT[Math.floor(Math.random() * ENCOURAGEMENT.length)];

  const selectedNeedData = VA_NEEDS.find(n => n.value === selectedNeed);

  const wizardRef = React.useRef(null);
  const scrollToTop = useCallback(() => {
    if (wizardRef.current) {
      wizardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const nextStep = useCallback(() => setCurrentStep(s => {
    const next = Math.min(s + 1, STEPS.length - 1);
    if (next !== s) setTimeout(scrollToTop, 50);
    return next;
  }), [scrollToTop]);
  const prevStep = useCallback(() => setCurrentStep(s => {
    const next = Math.max(s - 1, 0);
    if (next !== s) setTimeout(scrollToTop, 50);
    return next;
  }), [scrollToTop]);
  const goToStep = useCallback((i) => {
    setCurrentStep(i);
    setTimeout(scrollToTop, 50);
  }, [scrollToTop]);

  const canProceed = () => {
    if (currentStep === 0) return !!selectedNeed;
    if (currentStep === 1) return details.trim().length > 20;
    if (currentStep === 2) return serviceInfo.branch && serviceInfo.dischargeType;
    return true;
  };

  const handleGenerate = () => {
    const prefix = `[VA CLAIMS ASSISTANCE]\nNeed: ${selectedNeedData?.label || selectedNeed}\nBranch: ${serviceInfo.branch}\nYears of Service: ${serviceInfo.yearsOfService || 'Not specified'}\nDischarge: ${DISCHARGE_TYPES.find(d => d.value === serviceInfo.dischargeType)?.label || serviceInfo.dischargeType}\n${serviceInfo.serviceStart ? `Service Period: ${serviceInfo.serviceStart} - ${serviceInfo.serviceEnd || 'Present'}` : ''}\n\n`;
    const denialSection = denialText.trim() ? `\nDenial/Decision Letter Content:\n${denialText}\n\n` : '';
    const fullNotes = `${prefix}Veteran's Description:\n${details}${denialSection}`;
    
    // Configure generation flags based on the VA need type
    setNotes(fullNotes);
    
    // Map VA need to appropriate letter type
    const letterTypeMap = {
      new_claim: 'custom', increase_rating: 'custom', supplemental_claim: 'custom',
      higher_level_review: 'custom', medication_approval: 'custom',
      personal_statement: 'custom', buddy_statement: 'custom', records_request: 'custom',
    };
    setLetterType(letterTypeMap[selectedNeed] || 'custom');
    setGenerateAdminLetter(true);
    setLetterContext(`VA ${selectedNeedData?.label || 'Claims'} document for a ${serviceInfo.branch} Veteran`);
    
    // Also generate a portal message for medication approvals and records
    if (['medication_approval', 'records_request'].includes(selectedNeed)) {
      setDocumentMode('both');
      setMessageScenario(selectedNeed === 'medication_approval' ? 'medication_prior_auth' : 'records_request');
    } else {
      setDocumentMode('letter');
    }
    
    setTimeout(() => generateDocuments(), 300);
  };

  const handleCopy = (text, field) => {
    safeCopyToClipboard(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <Card className="border-green-600/30 bg-card" data-testid="va-claims-wizard" ref={wizardRef}>
      <CardContent className="p-4 sm:p-6 space-y-5">
        {/* Encouragement Banner */}
        <div className="p-3 bg-green-600/10 border border-green-600/30 rounded-lg" data-testid="va-encouragement">
          <p className="text-sm text-green-400 italic leading-relaxed">
            "{encouragement}"
          </p>
        </div>

        {/* Step Navigation */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const isActive = i === currentStep;
            const isDone = i < currentStep;
            return (
              <button key={step.id} onClick={() => goToStep(i)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  isActive ? 'bg-green-600 text-white shadow-sm' :
                  isDone ? 'bg-green-600/20 text-green-400' :
                  'text-muted-foreground hover:bg-secondary/50'
                }`}
                data-testid={`va-step-${step.id}`}
              >
                {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{step.label}</span>
                {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground/40 ml-1" />}
              </button>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="wizard-progress-bar" style={{ '--progress': `${((currentStep + 1) / STEPS.length) * 100}%` }} />

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: What You Need */}
          {currentStep === 0 && (
            <div className="space-y-4 wizard-step-enter">
              <div>
                <h3 className="text-lg font-semibold">What do you need help with?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Select the option that best fits your situation. We'll guide you through the right steps and forms.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {VA_NEEDS.map(need => {
                  const isSelected = selectedNeed === need.value;
                  return (
                    <button
                      key={need.value}
                      onClick={() => setSelectedNeed(need.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        isSelected
                          ? 'border-green-600 bg-green-600/10 shadow-sm'
                          : 'border-border hover:border-green-600/50 hover:bg-secondary/50'
                      }`}
                      data-testid={`va-need-${need.value}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${
                          isSelected ? 'bg-green-600 text-white' : 'bg-secondary text-muted-foreground'
                        }`}>
                          {need.icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-semibold break-words ${isSelected ? 'text-green-400' : 'text-foreground'}`}>
                            {need.label}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5 break-words">{need.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedNeedData && (
                <div className="p-3 bg-green-600/5 border border-green-600/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <div className="text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">What we'll need from you:</p>
                      <p>{selectedNeedData.guidance}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Your Story / Details */}
          {currentStep === 1 && (
            <div className="space-y-4 wizard-step-enter">
              <div>
                <h3 className="text-lg font-semibold">Tell Us Your Story</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedNeedData?.guidance || 'Describe your situation in your own words. Don\'t worry about medical terminology — just tell us what happened and how it affects you.'}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Your description — in your own words
                </Label>
                <Textarea
                  placeholder="Tell us what's going on. You can type or use voice-to-text. Don't worry about being perfect — just be specific about dates, symptoms, and how it affects your daily life..."
                  className="min-h-[160px] text-base bg-input"
                  value={details}
                  onChange={e => setDetails(e.target.value)}
                  data-testid="va-details-textarea"
                />
                <p className="text-xs text-muted-foreground">
                  {details.length < 20 ? `Keep going — the more detail you provide, the stronger your documents will be (${details.length}/20 min)` :
                    `Good detail. ${details.length} characters`}
                </p>
              </div>

              {/* Denial letter paste area — for appeals */}
              {(selectedNeed === 'supplemental_claim' || selectedNeed === 'higher_level_review' || selectedNeed === 'increase_rating' || selectedNeed === 'medication_approval') && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                    Paste your denial or decision letter (optional but powerful)
                  </Label>
                  <Textarea
                    placeholder="Paste the text from your VA denial letter, rating decision, or pharmacy rejection here. You can also use voice-to-text to read it aloud. This helps us understand exactly what VA said so we can address it point by point..."
                    className="min-h-[120px] text-sm bg-input border-amber-500/30"
                    value={denialText}
                    onChange={e => setDenialText(e.target.value)}
                    data-testid="va-denial-textarea"
                  />
                  <p className="text-xs text-muted-foreground italic">
                    This is the single most helpful thing you can provide for an appeal. We'll analyze it and help you respond point by point.
                  </p>
                </div>
              )}

              {/* Quick tips based on need */}
              <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                <p className="text-xs font-semibold text-green-500 flex items-center gap-1.5 mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> Tips for a Stronger Statement
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Be specific with dates — "In June 2019" is better than "a few years ago"</li>
                  <li>Describe what you <strong>can't do</strong>, not just what hurts</li>
                  <li>Mention how it affects your <strong>work, family, and daily activities</strong></li>
                  <li>If your condition has worsened, describe the <strong>before vs. now</strong></li>
                  {(selectedNeed === 'new_claim' || selectedNeed === 'supplemental_claim') && (
                    <li>Connect your condition to a <strong>specific event or duty during service</strong></li>
                  )}
                  {selectedNeed === 'medication_approval' && (
                    <li>List <strong>every alternative you've already tried</strong> and why they didn't work</li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {/* Step 3: Service Info */}
          {currentStep === 2 && (
            <div className="space-y-4 wizard-step-enter">
              <div>
                <h3 className="text-lg font-semibold">Your Service Background</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This helps us use the right language and reference the correct regulations for your situation.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Branch of Service *</Label>
                  <Select value={serviceInfo.branch} onValueChange={v => setServiceInfo(p => ({ ...p, branch: v }))}>
                    <SelectTrigger className="bg-input h-11" data-testid="va-branch-select">
                      <SelectValue placeholder="Select branch..." />
                    </SelectTrigger>
                    <SelectContent>
                      {BRANCHES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Years of Service</Label>
                  <Input
                    placeholder="e.g. 4, 8, 20"
                    value={serviceInfo.yearsOfService}
                    onChange={e => setServiceInfo(p => ({ ...p, yearsOfService: e.target.value }))}
                    className="h-11"
                    data-testid="va-years-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Discharge Type *</Label>
                  <Select value={serviceInfo.dischargeType} onValueChange={v => setServiceInfo(p => ({ ...p, dischargeType: v }))}>
                    <SelectTrigger className="bg-input h-11" data-testid="va-discharge-select">
                      <SelectValue placeholder="Select discharge..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCHARGE_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Approximate Service Dates</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Start (e.g. 2015)" value={serviceInfo.serviceStart}
                      onChange={e => setServiceInfo(p => ({ ...p, serviceStart: e.target.value }))}
                      className="h-11 flex-1" data-testid="va-start-input" />
                    <span className="self-center text-muted-foreground">to</span>
                    <Input placeholder="End (e.g. 2023)" value={serviceInfo.serviceEnd}
                      onChange={e => setServiceInfo(p => ({ ...p, serviceEnd: e.target.value }))}
                      className="h-11 flex-1" data-testid="va-end-input" />
                  </div>
                </div>
              </div>

              {serviceInfo.dischargeType && serviceInfo.dischargeType !== 'honorable' && serviceInfo.dischargeType !== 'still_serving' && serviceInfo.dischargeType !== 'medical' && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-400">
                    <strong>Your discharge type may affect eligibility</strong>, but does not automatically disqualify you.
                    Many Veterans with General or OTH discharges still qualify for benefits.
                    <a href="https://www.va.gov/discharge-upgrade-instructions/" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 ml-1 underline hover:text-amber-300">
                      Learn about discharge upgrades <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Results / Documents */}
          {currentStep === 3 && (
            <div className="space-y-4 wizard-step-enter">
              <div>
                <h3 className="text-lg font-semibold">Your Documents</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {result ? 'Your documents are ready. Copy them and use them in your VA claim.' :
                    'Review your information and click Generate to create your documents.'}
                </p>
              </div>

              {/* Review summary */}
              {!result && (
                <div className="space-y-2">
                  <div className="p-3 bg-secondary/50 rounded-lg border border-border space-y-2">
                    <ReviewLine label="Need" value={selectedNeedData?.label} onEdit={() => goToStep(0)} />
                    <ReviewLine label="Description" value={details.substring(0, 80) + (details.length > 80 ? '...' : '')} onEdit={() => goToStep(1)} />
                    {denialText && <ReviewLine label="Denial Letter" value="Included" onEdit={() => goToStep(1)} />}
                    <ReviewLine label="Branch" value={serviceInfo.branch} onEdit={() => goToStep(2)} />
                    <ReviewLine label="Service" value={serviceInfo.yearsOfService ? `${serviceInfo.yearsOfService} years` : 'Not specified'} onEdit={() => goToStep(2)} />
                    <ReviewLine label="Discharge" value={DISCHARGE_TYPES.find(d => d.value === serviceInfo.dischargeType)?.label} onEdit={() => goToStep(2)} />
                  </div>

                  <Button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="w-full h-12 text-base font-bold bg-green-600 hover:bg-green-700 text-white gap-2"
                    data-testid="va-generate-btn"
                  >
                    {isGenerating ? (
                      <>Generating your documents...</>
                    ) : (
                      <>
                        <FileText className="w-5 h-5" />
                        Generate My Documents
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Generated result */}
              {result?.admin_letter && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-green-500 font-semibold">Generated Document</Label>
                    <Button variant="outline" size="sm" className="gap-1.5 h-9"
                      onClick={() => handleCopy(result.admin_letter, 'va-letter')}
                      data-testid="va-copy-letter">
                      {copiedField === 'va-letter' ? <><Check className="w-3.5 h-3.5 text-green-500" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                    </Button>
                  </div>
                  <div className="p-4 bg-secondary/30 border border-border rounded-lg whitespace-pre-wrap text-sm leading-relaxed" data-testid="va-letter-output">
                    {result.admin_letter}
                  </div>
                </div>
              )}

              {result?.portal_message && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-blue-400 font-semibold">Portal Message</Label>
                    <Button variant="outline" size="sm" className="gap-1.5 h-9"
                      onClick={() => handleCopy(result.portal_message, 'va-msg')}
                      data-testid="va-copy-message">
                      {copiedField === 'va-msg' ? <><Check className="w-3.5 h-3.5 text-green-500" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                    </Button>
                  </div>
                  <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg whitespace-pre-wrap text-sm leading-relaxed" data-testid="va-message-output">
                    {result.portal_message}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-border/50">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
            className="gap-1.5 h-11 px-5 text-sm"
            data-testid="va-back-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <span className="text-sm text-muted-foreground">
            {currentStep + 1} of {STEPS.length}
          </span>
          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={nextStep}
              disabled={!canProceed()}
              className="gap-1.5 h-11 px-5 text-sm bg-green-600 hover:bg-green-700"
              data-testid="va-next-btn"
            >
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <div />
          )}
        </div>

        {/* VA Resources Links */}
        <div className="border-t border-border/50 pt-4">
          <p className="text-xs font-semibold text-green-500 mb-2 flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" /> VA Resources
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {VA_RESOURCES.map(r => (
              <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-secondary/50 transition-colors group">
                <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-green-500 shrink-0" />
                <div>
                  <span className="text-xs font-medium text-foreground group-hover:text-green-500">{r.label}</span>
                  <span className="text-[10px] text-muted-foreground ml-1.5">{r.desc}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ReviewLine = ({ label, value, onEdit }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <div className="flex items-center gap-2">
      <span className="font-medium text-foreground">{value || 'Not provided'}</span>
      <button onClick={onEdit} className="text-xs text-green-500 hover:underline">Edit</button>
    </div>
  </div>
);

export default VAClaimsWizard;
